import { useState, useCallback, useMemo } from 'react';

/**
 * useSoknadWizard - State management hook for the søknad wizard
 *
 * Manages:
 * - Current step index
 * - Stage data (checkboxes, free text, helper info per stage)
 * - Navigation (next/back)
 * - Data persistence shape for backend
 */
export function useSoknadWizard(config) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Subject of the søknad (PR: person-picker). WHO the application is about.
  // Defaults to the onboarded child (Tier 1) so, until the user picks, the
  // wizard behaves exactly as before. subjectIsOnboardedChild drives whether
  // the conditional Tier-2 confirm step is part of the flow (see isStepActive).
  const [subject, setSubjectState] = useState({
    subjectEntityId: null,
    subjectName: null,
    subjectIsOnboardedChild: true,
    subjectIdentity: null,
  });

  const [stageData, setStageData] = useState(() => {
    // Initialize empty stage data for each step
    const initial = {};
    if (config?.steps) {
      config.steps.forEach(step => {
        initial[step.id] = {
          checkedItems: [],
          freeText: '',
          primaryHelper: '',
          estimatedHours: '',
          gateAnswer: null, // for gate questions: true/false/null
          conditionalData: {}, // for conditional follow-up fields
        };
      });
    }
    return initial;
  });

  // Raw config length. currentStepIndex is ALWAYS an index into the UNFILTERED
  // config.steps array — this is the single source of truth that keeps the
  // SummaryStep edit-jump (resolved by id) and draft resume indices stable. We
  // never reindex config.steps for conditional steps; instead we SKIP inactive
  // ones during navigation and exclude them from the displayed count.
  const stepCount = config?.steps?.length || 0;

  // Is a step part of the flow right now? The Tier-2 confirm step
  // (isSubjectConfirmStep) is active ONLY when the subject is NOT the onboarded
  // child. Every other step is always active. Keeping the step in config.steps
  // at a fixed index (rather than splicing it in/out) means indices never shift,
  // so no fragile offset is needed anywhere.
  const isStepActive = useCallback((step) => {
    if (!step) return false;
    if (step.isSubjectConfirmStep) {
      return subject.subjectIsOnboardedChild === false;
    }
    return true;
  }, [subject.subjectIsOnboardedChild]);

  // The visible flow: config.steps minus any inactive conditional steps. Used
  // ONLY for the displayed count / progress / dots — never for navigation
  // indexing (that stays on the raw config index).
  const activeSteps = useMemo(
    () => (config?.steps || []).filter(isStepActive),
    [config, isStepActive]
  );
  const totalSteps = activeSteps.length;

  const currentStep = config?.steps?.[currentStepIndex] || null;

  // Find the next/previous ACTIVE config index from a given position.
  const nextActiveIndex = useCallback((fromIndex) => {
    for (let i = fromIndex + 1; i < stepCount; i++) {
      if (isStepActive(config.steps[i])) return i;
    }
    return -1;
  }, [config, stepCount, isStepActive]);

  const prevActiveIndex = useCallback((fromIndex) => {
    for (let i = fromIndex - 1; i >= 0; i--) {
      if (isStepActive(config.steps[i])) return i;
    }
    return -1;
  }, [config, isStepActive]);

  // First step = no active step before the current one. Last step = none after.
  const isFirstStep = prevActiveIndex(currentStepIndex) === -1;
  const isLastStep = nextActiveIndex(currentStepIndex) === -1;

  // Progress percentage (0 to 100) over the VISIBLE steps. The current step's
  // position among active steps drives the bar so a skipped conditional step
  // doesn't leave a gap.
  const activePosition = activeSteps.findIndex(s => s?.id === currentStep?.id);
  const progress = totalSteps > 1 && activePosition >= 0
    ? Math.round((activePosition / (totalSteps - 1)) * 100)
    : 0;

  // Navigation — all skip inactive steps so the conditional confirm step is
  // never landed on when it isn't part of the flow.
  const goToNext = useCallback(() => {
    const next = nextActiveIndex(currentStepIndex);
    if (next !== -1) setCurrentStepIndex(next);
  }, [currentStepIndex, nextActiveIndex]);

  const goBack = useCallback(() => {
    const prev = prevActiveIndex(currentStepIndex);
    if (prev !== -1) setCurrentStepIndex(prev);
  }, [currentStepIndex, prevActiveIndex]);

  const goToStep = useCallback((index) => {
    if (index < 0 || index >= stepCount) return;
    const target = config.steps[index];
    // If asked to jump to an inactive step (shouldn't happen via the UI, but
    // guards resume/edit-jump against a stale index), land on the nearest
    // active step at or after it.
    if (isStepActive(target)) {
      setCurrentStepIndex(index);
    } else {
      const fallback = nextActiveIndex(index);
      if (fallback !== -1) setCurrentStepIndex(fallback);
    }
  }, [config, stepCount, isStepActive, nextActiveIndex]);

  // Update the picked subject. Merges so partial updates (e.g. just the
  // confirmed identity facts) don't drop the name/id.
  const setSubject = useCallback((fields) => {
    setSubjectState(prev => ({ ...prev, ...fields }));
  }, []);

  // Toggle a checkbox item for current stage
  const toggleCheckbox = useCallback((itemId) => {
    if (!currentStep) return;

    setStageData(prev => {
      const stageId = currentStep.id;
      const currentChecked = prev[stageId]?.checkedItems || [];
      const isChecked = currentChecked.includes(itemId);

      return {
        ...prev,
        [stageId]: {
          ...prev[stageId],
          checkedItems: isChecked
            ? currentChecked.filter(id => id !== itemId)
            : [...currentChecked, itemId],
        },
      };
    });
  }, [currentStep]);

  // Set free text for current stage
  const setFreeText = useCallback((text) => {
    if (!currentStep) return;

    setStageData(prev => ({
      ...prev,
      [currentStep.id]: {
        ...prev[currentStep.id],
        freeText: text,
      },
    }));
  }, [currentStep]);

  // Set helper fields for current stage
  const setHelperFields = useCallback((helper, hours) => {
    if (!currentStep) return;

    setStageData(prev => ({
      ...prev,
      [currentStep.id]: {
        ...prev[currentStep.id],
        primaryHelper: helper,
        estimatedHours: hours,
      },
    }));
  }, [currentStep]);

  // Set gate answer for current stage
  const setGateAnswer = useCallback((answer) => {
    if (!currentStep) return;

    setStageData(prev => ({
      ...prev,
      [currentStep.id]: {
        ...prev[currentStep.id],
        gateAnswer: answer,
      },
    }));
  }, [currentStep]);

  // Set conditional data (for follow-up questions on checkboxes)
  const setConditionalData = useCallback((key, value) => {
    if (!currentStep) return;

    setStageData(prev => ({
      ...prev,
      [currentStep.id]: {
        ...prev[currentStep.id],
        conditionalData: {
          ...prev[currentStep.id]?.conditionalData,
          [key]: value,
        },
      },
    }));
  }, [currentStep]);

  // Update a specific stage's data (for loading from draft)
  const updateStageData = useCallback((stageId, data) => {
    setStageData(prev => ({
      ...prev,
      [stageId]: {
        ...prev[stageId],
        ...data,
      },
    }));
  }, []);

  // Get current stage data
  const currentStageData = useMemo(() => {
    if (!currentStep) return null;
    return stageData[currentStep.id] || {};
  }, [currentStep, stageData]);

  // Check if current step has any selections
  const hasSelections = useMemo(() => {
    if (!currentStageData) return false;
    return currentStageData.checkedItems?.length > 0 ||
           currentStageData.freeText?.trim().length > 0;
  }, [currentStageData]);

  // Get all stage data formatted for backend
  const getAllStageData = useCallback(() => {
    return stageData;
  }, [stageData]);

  // Reset all data
  const resetWizard = useCallback(() => {
    setCurrentStepIndex(0);
    const initial = {};
    if (config?.steps) {
      config.steps.forEach(step => {
        initial[step.id] = {
          checkedItems: [],
          freeText: '',
          primaryHelper: '',
          estimatedHours: '',
          gateAnswer: null,
          conditionalData: {},
        };
      });
    }
    setStageData(initial);
  }, [config]);

  return {
    // Current state
    currentStepIndex,
    currentStep,
    totalSteps,
    progress,
    isFirstStep,
    isLastStep,
    currentStageData,
    hasSelections,
    stageData,

    // Subject of the søknad (PR: person-picker)
    subject,
    setSubject,
    activeSteps,
    isStepActive,

    // Navigation
    goToNext,
    goBack,
    goToStep,

    // Data mutations
    toggleCheckbox,
    setFreeText,
    setHelperFields,
    setGateAnswer,
    setConditionalData,
    updateStageData,
    getAllStageData,
    resetWizard,
  };
}

export default useSoknadWizard;
